import { AxiosGlobal } from "../shared/axios";
import { AxiosPromise } from "axios";
import configs from "../../configs";
import { store } from "../../store/store";

export class RiderLiveApi extends AxiosGlobal {

    // ==================== PUBLIC (NO AUTH) ====================

    /**
     * Rider self-registration (multipart/form-data)
     */
    registerRider(data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/rider/register`, data, {
            headers: {
                "Content-Type": "multipart/form-data",
                "Accept": "application/json",
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Check rider application status by email
     */
    checkApplicationStatus(data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/rider/check-status`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Access-Control-Allow-Origin": "*"
            },
        });
    }


    getPinStatus(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/rider/withdrawal-pin/status`, {
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${store.getState().data.login.value.token}` },
        });
    }

    setWithdrawalPin(data: { pin: string; pin_confirmation: string }): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/rider/withdrawal-pin/set`, data, {
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${store.getState().data.login.value.token}` },
        });
    }

    changeWithdrawalPin(data: { current_pin: string; new_pin: string; new_pin_confirmation: string }): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/rider/withdrawal-pin/change`, data, {
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${store.getState().data.login.value.token}` },
        });
    }



    /**
         * Fleet agent creates a rider under their account.
         * POST /api/rider/fleet/create-rider
         * Supports multipart/form-data for file uploads.
         */
    createFleetRider(data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/rider/fleet/create-rider`, data, {
            headers: {
                "Content-Type": "multipart/form-data",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get all riders under this fleet agent.
     * GET /api/rider/fleet/my-riders?status=&search=&per_page=15
     */
    getFleetRiders(query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        if (query.page) params.append('page', query.page);
        if (query.per_page) params.append('per_page', query.per_page);
        if (query.status && query.status !== 'all') params.append('status', query.status);
        if (query.search) params.append('search', query.search);

        return this.axios.get(`${configs.context}/rider/fleet/my-riders?${params.toString()}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get fleet dashboard stats (rider counts, wallet, pending payments).
     * GET /api/rider/fleet/stats
     */
    getFleetStats(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/rider/fleet/stats`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get a single rider's details (fleet can only view own riders).
     * GET /api/rider/fleet/rider/{riderId}
     */
    getFleetRiderDetail(riderId: number, params?: any): AxiosPromise<any> {
        const query = new URLSearchParams();
        if (params?.page) query.append('page', params.page);
        if (params?.per_page) query.append('per_page', params.per_page);
        if (params?.payment_status && params.payment_status !== 'all') query.append('payment_status', params.payment_status);
        if (params?.start_date) query.append('start_date', params.start_date);
        if (params?.end_date) query.append('end_date', params.end_date);

        const queryString = query.toString();
        const url = `${configs.context}/rider/fleet/rider/${riderId}${queryString ? '?' + queryString : ''}`;

        return this.axios.get(url, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }


    // ==================== ADMIN: RIDER APPLICATIONS ====================

    /**
     * Get all rider applications with filters
     */
    getAllRiderApplications(query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        if (query.page) params.append('page', query.page);
        if (query.per_page) params.append('per_page', query.per_page);
        if (query.status && query.status !== 'all') params.append('status', query.status);
        if (query.dojah_status && query.dojah_status !== 'all') params.append('dojah_status', query.dojah_status);
        if (query.flagged !== undefined && query.flagged !== '') params.append('flagged', query.flagged);
        if (query.search) params.append('search', query.search);
        if (query.fleet_id) params.append('fleet_id', query.fleet_id);
        if (query.start_date) params.append('start_date', query.start_date);
        if (query.end_date) params.append('end_date', query.end_date);

        return this.axios.get(`${configs.context}/admin/rider-applications?${params.toString()}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get rider application stats for dashboard
     */
    getRiderApplicationStats(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/admin/rider-applications/stats`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get single rider application details
     */
    getRiderApplicationDetails(id: number): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/admin/rider-applications/${id}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Approve a rider application
     */
    approveRiderApplication(id: number, data: any): AxiosPromise<any> {
        return this.axios.put(`${configs.context}/admin/rider-applications/${id}/approve`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Reject a rider application
     */
    rejectRiderApplication(id: number, data: any): AxiosPromise<any> {
        return this.axios.put(`${configs.context}/admin/rider-applications/${id}/reject`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Update rider rate per shipment
     */
    updateRiderRate(id: number, data: any): AxiosPromise<any> {
        return this.axios.put(`${configs.context}/admin/rider-applications/${id}/update-rate`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Retry Dojah NIN verification
     */
    retryDojahVerification(id: number): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/admin/rider-applications/${id}/retry-verification`, {}, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Toggle rider flag (flag/unflag)
     */
    toggleRiderFlag(id: number, data: any): AxiosPromise<any> {
        return this.axios.put(`${configs.context}/admin/rider-applications/${id}/toggle-flag`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    // ==================== RIDER: WALLET ====================

    /**
     * Get rider wallet dashboard
     */
    getRiderWalletDashboard(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/rider/wallet/dashboard`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get rider wallet transactions
     */
    getRiderTransactions(query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        if (query.page) params.append('page', query.page);
        if (query.type && query.type !== 'all') params.append('type', query.type);
        if (query.status && query.status !== 'all') params.append('status', query.status);

        return this.axios.get(`${configs.context}/rider/wallet/transactions?${params.toString()}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Request withdrawal
     */
    // requestWithdrawal(data: any): AxiosPromise<any> {
    //     return this.axios.post(`${configs.context}/rider/wallet/withdraw`, data, {
    //         headers: {
    //             "Content-Type": "application/json",
    //             "Accept": "application/json",
    //             "Authorization": `Bearer ${store.getState().data.login.value.token}`,
    //             "Access-Control-Allow-Origin": "*"
    //         },
    //     });
    // }

    requestWithdrawal(data: { amount: string; pin: string }): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/rider/wallet/withdraw`, data, {
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${store.getState().data.login.value.token}` },
        });
    }

    /**
     * Get rider's bank accounts
     */
    getRiderBankAccounts(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/rider/wallet/bank-accounts`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get list of banks (for dropdown)
     * GET /api/rider/wallet/banks
     */
    getBankList(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/rider/wallet/banks`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Add/update bank account (verifies via Paystack)
     * POST /api/rider/wallet/add-bank
     * Body: { bank_code, bank_name, account_number }
     */
    addBankAccount(data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/rider/wallet/add-bank`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    // ==================== ADMIN: WALLET MANAGEMENT ====================

    /**
     * Get all rider wallets (admin)
     */
    getAllRiderWallets(query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        if (query.page) params.append('page', query.page);
        if (query.search) params.append('search', query.search);
        if (query.status && query.status !== 'all') params.append('status', query.status);

        return this.axios.get(`${configs.context}/admin/wallets?${params.toString()}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get pending withdrawals (admin)
     */
    getPendingWithdrawals(query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        if (query.page) params.append('page', query.page);

        return this.axios.get(`${configs.context}/admin/wallets/pending-withdrawals?${params.toString()}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Approve withdrawal (admin)
     */
    approveWithdrawal(id: number): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/admin/wallets/withdrawals/${id}/approve`, {}, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Reject withdrawal (admin)
     */
    rejectWithdrawal(id: number, data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/admin/wallets/withdrawals/${id}/reject`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    // ==================== ADMIN: FRAUD DASHBOARD ====================

    /**
     * Get fraud dashboard stats
     */
    getFraudDashboard(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/admin/fraud/dashboard`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get risky riders list
     */
    getRiskyRiders(query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        if (query.page) params.append('page', query.page);
        if (query.per_page) params.append('per_page', query.per_page);
        if (query.min_deliveries) params.append('min_deliveries', query.min_deliveries);

        return this.axios.get(`${configs.context}/admin/fraud/risky-riders?${params.toString()}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get rider trust score breakdown
     */
    getRiderTrustScore(userId: number): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/admin/fraud/rider/${userId}/trust-score`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get active disputes
     */
    getActiveDisputes(query: any): AxiosPromise<any> {
        const params = new URLSearchParams();
        if (query.page) params.append('page', query.page);
        if (query.per_page) params.append('per_page', query.per_page);
        if (query.rider_id) params.append('rider_id', query.rider_id);
        if (query.search) params.append('search', query.search);

        return this.axios.get(`${configs.context}/admin/fraud/disputes?${params.toString()}`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Bulk resolve disputes
     */
    bulkResolveDisputes(data: any): AxiosPromise<any> {
        return this.axios.post(`${configs.context}/admin/fraud/disputes/bulk-resolve`, data, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }

    /**
     * Get fraud settings
     */
    getFraudSettings(): AxiosPromise<any> {
        return this.axios.get(`${configs.context}/admin/fraud/settings`, {
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "Authorization": `Bearer ${store.getState().data.login.value.token}`,
                "Access-Control-Allow-Origin": "*"
            },
        });
    }
}